import React, { useEffect } from 'react';
import type { KeyboardEvent } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import CrossIcon from '@atlaskit/icon/core/cross';

import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { TRIAL_COMPONENTS_EXPERIENCES } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import { CompliantWarningBanner, BannerLink } from './CompliantWarningBanner.tsx';
// eslint-disable-next-line import/no-cycle
import { COMPLIANT_BANNER_HEIGHT } from './GracePeriodBanner.tsx';

const i18n = defineMessages({
	trialEndedWarning: {
		id: 'trial-components.trial.ended.warning',
		defaultMessage: 'Your trial has ended.',
		description: 'Inform the user that their trial has ended',
	},
	remainingDaysWarning: {
		id: 'trial-components.trial.ended.remaining.days.warning',
		defaultMessage:
			'We’re giving you an extra {numberOfDaysLeft, plural, one {# day} other {# days}} to keep all {edition} features.',
		description:
			'Let the user know how many days they have left before they get downgraded to Free plan',
	},
	lastDayWarning: {
		id: 'trial-components.trial.ended.last.day.warning',
		defaultMessage: 'Today is the last day to keep all {edition} features.',
		description: 'Remind the user that it is their last day of their trial',
	},
	linkToPaymentDetails: {
		id: 'trial-components.banner.trial.ended.link-to-payment-details',
		defaultMessage: 'Add payment details to stay on {edition}.',
		description: 'CTA link to add payment details',
	},
	dismissIcon: {
		id: 'trial-components.banner.icons.dismiss',
		defaultMessage: 'Dismiss button with cross icon',
		description: 'Accessibility label for an dismiss icon',
	},
	reverseTrialsChooseAPlanMessage: {
		id: 'trial-components.banner.compliant.reverse-trials.choose-a-plan.message',
		defaultMessage:
			'Your trial has ended. We’re giving you {numberOfDaysLeft, plural, one {# day} other {# days}} to keep all features so you can choose the right plan for your team.',
		description:
			'Inform the user that their trial has ended and they have a grace period to choose a plan.',
	},
	reverseTrialsChooseAPlanCTA: {
		id: 'trial-components.banner.compliant.reverse-trials.choose-a-plan.cta',
		defaultMessage: 'Choose plan now',
		description: 'CTA text instructing the user to choose a plan',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	cursor: 'pointer',
});

const capitalize = (string) => {
	const lowerCaseString = string.toLowerCase();
	return lowerCaseString.charAt(0).toUpperCase() + lowerCaseString.slice(1);
};

interface CompliantBannerData {
	linkToAddBilling: string;
	linkToChoosePlan?: string;
	daysLeftUntilDeactivation?: number;
	licenseConsumingUserCount: number;
	edition: string;
	hideBanner: () => void;
	premiumReverseTrialABCohort?: string;
}

export const CompliantBannerMessage = ({
	linkToAddBilling,
	daysLeftUntilDeactivation,
	licenseConsumingUserCount,
	edition,
	hideBanner,
	premiumReverseTrialABCohort,
	linkToChoosePlan,
}: CompliantBannerData) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setOnboardingState } = useSetOnboardingState();
	const { formatMessage } = useIntl();

	useEffect(() => {
		if (premiumReverseTrialABCohort) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'compliantGracePeriodBanner',
					attributes: {
						daysLeft: daysLeftUntilDeactivation,
						licenseConsumingUserCount,
						edition,
						premiumReverseTrialABCohort,
					},
				},
			}).fire();
		}
	}, [
		createAnalyticsEvent,
		daysLeftUntilDeactivation,
		licenseConsumingUserCount,
		edition,
		premiumReverseTrialABCohort,
	]);

	const onBillingClicked = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'gracePeriodBannerBilling',
				source: 'compliantGracePeriodBanner',
				attributes: {
					daysLeft: daysLeftUntilDeactivation,
					licenseConsumingUserCount,
					edition,
					premiumReverseTrialABCohort,
				},
			},
		}).fire();
	};

	const onChoosePlanClicked = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'compliantGracePeriodBannerChoosePlanCTA',
				source: 'compliantGracePeriodBanner',
				attributes: {
					daysLeft: daysLeftUntilDeactivation,
					licenseConsumingUserCount,
					premiumReverseTrialABCohort,
				},
			},
		}).fire();
	};

	const onDismiss = () => {
		void setOnboardingState({
			key: TRIAL_COMPONENTS_EXPERIENCES.GRACE_PERIOD_BANNER_DISMISSED,
			value: 'true',
		});
		hideBanner();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'button',
				actionSubjectId: 'compliantGracePeriodBanner',
				source: 'compliantGracePeriodBanner',
				attributes: {
					daysLeft: daysLeftUntilDeactivation,
					licenseConsumingUserCount,
					edition,
					premiumReverseTrialABCohort,
				},
			},
		}).fire();
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		// can select an option with either Enter or spacebar
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			onDismiss();
		}
	};

	if (
		daysLeftUntilDeactivation === undefined ||
		isNaN(daysLeftUntilDeactivation) ||
		daysLeftUntilDeactivation < 0
	) {
		return null;
	}

	if (premiumReverseTrialABCohort === 'variation') {
		return (
			<CompliantWarningBanner
				bannerHeight={COMPLIANT_BANNER_HEIGHT}
				description={
					<FormattedMessage
						{...i18n.reverseTrialsChooseAPlanMessage}
						values={{ numberOfDaysLeft: daysLeftUntilDeactivation }}
					/>
				}
				inlineDescriptionCTA={
					<BannerLink href={linkToChoosePlan} target="_blank" onClick={onChoosePlanClicked}>
						<FormattedMessage {...i18n.reverseTrialsChooseAPlanCTA} />
					</BannerLink>
				}
				rightSideComponent={
					<IconWrapper
						onClick={onDismiss}
						tabIndex={0}
						data-testid="banner-dismiss-icon"
						onKeyDown={handleKeyDown}
					>
						<CrossIcon label={formatMessage(i18n.dismissIcon)} />
					</IconWrapper>
				}
			/>
		);
	}

	return (
		<CompliantWarningBanner
			bannerHeight={COMPLIANT_BANNER_HEIGHT}
			description={
				<>
					<FormattedMessage {...i18n.trialEndedWarning} />{' '}
					{daysLeftUntilDeactivation === 0 ? (
						<FormattedMessage
							{...i18n.lastDayWarning}
							values={{
								edition: capitalize(edition),
							}}
						/>
					) : (
						<FormattedMessage
							{...i18n.remainingDaysWarning}
							values={{
								numberOfDaysLeft: daysLeftUntilDeactivation,
								edition: capitalize(edition),
							}}
						/>
					)}
				</>
			}
			inlineDescriptionCTA={
				<BannerLink href={linkToAddBilling} target="_blank" onClick={onBillingClicked}>
					<FormattedMessage
						{...i18n.linkToPaymentDetails}
						values={{
							edition: capitalize(edition),
						}}
					/>
				</BannerLink>
			}
			rightSideComponent={
				<IconWrapper
					onClick={onDismiss}
					tabIndex={0}
					data-testid="banner-dismiss-icon"
					onKeyDown={handleKeyDown}
				>
					<CrossIcon label={formatMessage(i18n.dismissIcon)} />
				</IconWrapper>
			}
		/>
	);
};
